import { Box } from '@mui/material'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { isArray } from 'lodash'
import { ConstrainedTimeGraph } from './ConstrainedTime.graph'
import { StockProjectionExcessGraph } from './StockProjectionExcess.graph'
import {
  StockBuildPageParams,
  StockBuildPageProps,
  StockBuildTableData,
} from './StockBuild.types'
import {
  useConstrainedData,
  useTableData,
  useExcessData,
} from './StockBuild.data'
import { StockBuildMUITable } from './StockBuild.MUITable'

export const StockBuildPage = ({}: StockBuildPageProps) => {
  const [selectedSku, setSelectedSku] = useState<null | string>(null)
  const handleRowClick = (row: StockBuildTableData[] | StockBuildTableData) => {
    if (isArray(row)) {
      const [rowData] = row
      if (rowData && rowData.skuCode) {
        const skuCode: string = rowData.skuCode
        setSelectedSku((prevSku: string | null) => {
          if (prevSku === skuCode) {
            return null
          }
          return skuCode
        })
      }
    }
  }

  const { planId, sourcesiteId, workcentreId } =
    useParams<StockBuildPageParams>()

  const { data, isLoading: isCapacityLoading } = useConstrainedData(
    planId,
    sourcesiteId,
    workcentreId,
    selectedSku
  )

  const { data: tableData, isLoading: isTableDataLoading } = useTableData(
    planId,
    sourcesiteId,
    workcentreId
  )

  const { data: ExcessData, isLoading: isExcessLoading } = useExcessData(
    planId,
    sourcesiteId,
    workcentreId,
    selectedSku
  )

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box height={300} mb={2}>
        <ConstrainedTimeGraph
          isLoading={isCapacityLoading}
          traceOneName="Work centre capacity"
          traceTwoName="Unconstrained hours"
          traceThreeName="Constrained hours increased"
          traceFourName="Constrained hours decreased"
          traceFiveName="Constrained hours unchanged"
          xAxisData={data?.productionDate ?? []}
          yAxisDataOne={data?.workCentreCapacity ?? []}
          yAxisDataTwo={data?.unconstrainedHours ?? []}
          yAxisDataThree={data?.constrainedHoursIncreased ?? []}
          yAxisDataFour={data?.constrainedHoursDecreased ?? []}
          yAxisDataFive={data?.constrainedHoursUnchanged ?? []}
        />
      </Box>

      <Box height={300} mb={2}>
        <StockProjectionExcessGraph
          isLoading={isExcessLoading}
          traceOneName={'Excess'}
          traceTwoName={'Minimum Inventory'}
          traceThreeName={'Safety Stock'}
          traceFourName={'Week Start Inventory'}
          xAxisData={ExcessData?.productionDate ?? []}
          excess={ExcessData?.excess ?? []}
          minimumInventory={ExcessData?.constrainedHours ?? []}
          safetyStockVolume={ExcessData?.safetyStockVolume ?? []}
          weekStartInventory={ExcessData?.weekStartInventory ?? []}
        />
      </Box>

      <Box>
        <StockBuildMUITable
          data={tableData?.data ?? []}
          isLoading={isTableDataLoading}
          handleRowClick={handleRowClick}
          skuCodes={tableData?.skuCodes ?? []}
          skuNames={tableData?.skuNames ?? []}
          siteCodes={tableData?.siteCodes ?? []}
        />
      </Box>
    </Box>
  )
}
